//! Shared formula-bar / inline-editor helpers for autocomplete and function hints.

import {
  COMPLETION_SECTION_NAMED_RANGES,
  COMPLETION_SECTION_RECENT,
  getFormulaCompletions,
  getFunctionHover,
  type FormulaCompletion,
} from '@/app/cellEditor/cellEditorLanguage';
import { referenceAtCaret, wantsCellRefAtCaret } from '@/app/cellEditor/cellEditorFormula';
import { getRecentFunctionNames } from '@/app/cellEditor/recentFormulaFunctions';
import { sheets } from '@/app/grid/controller/Sheets';
import { content } from '@/app/gridGL/pixiApp/contentInstance';
import type { JsDefinedName } from '@/app/quadratic-core-types';
import type { SheetPosTS } from '@/app/shared/types/size';
import { quadraticCore } from '@/app/web-workers/quadraticCore/quadraticCore';

/// Returns the anchor position of a formula cell that intersects `pos`. For
/// multi-cell formula output, editing must happen at the anchor, not on an
/// output cell.
export function getFormulaAnchorAt(sheetId: string, x: number, y: number): SheetPosTS | null {
  const codeCell = content.cellsSheet.tables.getCodeCellIntersects({ x, y });
  if (codeCell?.language !== 'Formula') return null;
  return { sheetId, x: codeCell.x, y: codeCell.y };
}

/// Returns the editable formula text (`=...`) for a cell that contains a formula,
/// even when the cell's displayed/output value is the computed result.
export async function getFormulaSourceForCell(
  sheetId: string,
  x: number,
  y: number
): Promise<{ formulaText: string; error: string | null } | null> {
  try {
    const formulaCell = await quadraticCore.getCodeCell(sheetId, x, y);
    if (formulaCell?.language === 'Formula') {
      return {
        formulaText: `=${formulaCell.code_string}`,
        error: formulaCell.std_err ?? null,
      };
    }
    const editCell = await quadraticCore.getEditCell(sheetId, x, y);
    if (editCell?.codeCell?.language === 'Formula') {
      return {
        formulaText: `=${editCell.codeCell.code}`,
        error: null,
      };
    }
  } catch (_e) {
    // ignore
  }
  return null;
}

export const MAX_CELL_EDITOR_AUTOCOMPLETE_ITEMS = 50;

/// Recently used functions, re-tagged into their own dropdown section. Only
/// names that still resolve to a known function are included.
const getRecentFunctionCompletions = (): FormulaCompletion[] => {
  const byLabel = new Map(getFormulaCompletions().map((c) => [c.label, c]));
  return getRecentFunctionNames()
    .map((name) => byLabel.get(name))
    .filter((c): c is FormulaCompletion => c !== undefined)
    .map((c) => ({ ...c, section: COMPLETION_SECTION_RECENT }));
};

/// Defined names (named ranges/constants) visible from the current sheet, as
/// completions. Sheet-scoped names shadow workbook-scoped names of the same
/// name, matching how formulas resolve them.
const getNamedRangeCompletions = (): FormulaCompletion[] => {
  try {
    const sheetId = sheets.current;
    const visible = new Map<string, JsDefinedName>();
    for (const dn of sheets.getDefinedNames()) {
      // scope is null/undefined for workbook-scoped names (the wasm layer
      // serializes Rust's `None` as undefined).
      if (dn.scope != null && dn.scope !== sheetId) continue;
      const key = dn.name.toLowerCase();
      const existing = visible.get(key);
      if (!existing || (existing.scope == null && dn.scope != null)) {
        visible.set(key, dn);
      }
    }
    return [...visible.values()]
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((dn) => ({
        label: dn.name,
        detail: dn.formula,
        documentation: dn.comment ?? '',
        insertText: dn.name,
        kind: 'definedName' as const,
        section: COMPLETION_SECTION_NAMED_RANGES,
      }));
  } catch (_e) {
    return [];
  }
};

/// Builds the full sectioned completion list (Recent functions, Named ranges,
/// Functions), optionally filtered by a lowercase prefix. Sections with no
/// matching items contribute nothing, so empty sections never render.
const composeCompletionItems = (lowerPrefix?: string): FormulaCompletion[] => {
  const matches = (c: FormulaCompletion) => !lowerPrefix || c.label.toLowerCase().startsWith(lowerPrefix);
  return [
    ...getRecentFunctionCompletions().filter(matches),
    ...getNamedRangeCompletions().filter(matches),
    ...getFormulaCompletions().filter(matches),
  ];
};

// Returns the partial identifier being typed immediately before the caret.
// `?` is a valid defined-name character after the first (Excel rule), so it
// must be part of the word for names like `Taxed?` to autocomplete.
export const currentWordAtCaret = (value: string, caret: number): { word: string; start: number } => {
  const match = value.slice(0, caret).match(/[A-Za-z_][A-Za-z0-9_.?]*$/);
  if (!match) return { word: '', start: caret };
  return { word: match[0], start: caret - match[0].length };
};

// Returns the name of the function whose argument list encloses the caret.
export const enclosingFunctionAtCaret = (value: string, caret: number): string | undefined => {
  let depth = 0;
  for (let i = caret - 1; i >= 0; i--) {
    const ch = value[i];
    if (ch === ')') depth++;
    else if (ch === '(') {
      if (depth === 0) {
        const before = value.slice(0, i).match(/[A-Za-z_][A-Za-z0-9_.]*$/);
        return before ? before[0] : undefined;
      }
      depth--;
    }
  }
  return undefined;
};

const enclosingFunctionNameRange = (
  value: string,
  caret: number,
  functionName: string
): { start: number; end: number } | undefined => {
  let depth = 0;
  for (let i = caret - 1; i >= 0; i--) {
    const ch = value[i];
    if (ch === ')') depth++;
    else if (ch === '(') {
      if (depth === 0) {
        const start = i - functionName.length;
        if (start >= 0 && value.slice(start, i) === functionName) {
          return { start, end: i };
        }
        return undefined;
      }
      depth--;
    }
  }
  return undefined;
};

/// Returns the span to replace when accepting a formula function completion.
export const getCompletionReplaceRange = (
  value: string,
  caret: number,
  selectionEnd: number = caret
): { start: number; end: number } => {
  if (selectionEnd > caret) {
    return { start: caret, end: selectionEnd };
  }

  const enclosingFn = enclosingFunctionAtCaret(value, caret);
  if (enclosingFn) {
    const fnRange = enclosingFunctionNameRange(value, caret, enclosingFn);
    if (fnRange) return fnRange;
  }

  const { word, start: wordStart } = currentWordAtCaret(value, caret);
  if (word) {
    let end = caret;
    while (end < value.length && /[A-Za-z0-9_.?]/.test(value[end])) {
      end++;
    }
    return { start: wordStart, end };
  }

  return { start: caret, end: caret };
};

export interface CellEditorLanguageState {
  items: FormulaCompletion[];
  index: number;
  functionHint: string;
}

export const emptyCellEditorLanguageState = (): CellEditorLanguageState => ({
  items: [],
  index: 0,
  functionHint: '',
});

/// Updates autocomplete + function-hint state for a formula value at the caret.
export const computeCellEditorLanguageState = (
  value: string,
  caret: number,
  forceAutocomplete = false
): CellEditorLanguageState => {
  const isFormula = value.startsWith('=');
  if (!isFormula) {
    return emptyCellEditorLanguageState();
  }

  const { word } = currentWordAtCaret(value, caret);
  const nextChar = value[caret];
  const enclosingFn = enclosingFunctionAtCaret(value, caret);
  const functionHint = enclosingFn ? (getFunctionHover(enclosingFn) ?? '') : '';

  if (forceAutocomplete) {
    return { items: composeCompletionItems(), index: 0, functionHint: '' };
  }

  // Don't show autocomplete when editing cell references. Check the FULL token
  // containing the cursor (not just the part before it) to detect cases like
  // editing "A|5" where the cursor is between the letter and digit.
  const tokenAtCaret = referenceAtCaret(value, caret);
  const isEditingCellRef =
    (tokenAtCaret && /\d/.test(tokenAtCaret.text)) || // Full token has digit (e.g., "A5", "B20")
    wantsCellRefAtCaret(value, caret); // Position expects cell ref (e.g., after "=")

  if (isEditingCellRef) {
    return { items: [], index: 0, functionHint };
  }

  const prefix = word.length >= 1 && nextChar !== '(' ? word : enclosingFn;
  if (prefix) {
    const lower = prefix.toLowerCase();
    const items = composeCompletionItems(lower).slice(0, MAX_CELL_EDITOR_AUTOCOMPLETE_ITEMS);
    return { items, index: 0, functionHint: items.length > 0 ? '' : functionHint };
  }

  return { items: [], index: 0, functionHint };
};
